'use client';

import { useTheme } from 'next-themes';
import {
  type ExternalToast,
  Toaster as Sonner,
  toast as sonnerToast,
} from 'sonner';

import { sanitizeUserFacingText } from '@kit/shared/upstream-error';

type ToasterProps = React.ComponentProps<typeof Sonner>;

function presentErrorText<T>(message: T): T {
  if (typeof message !== 'string') {
    return message;
  }

  return sanitizeUserFacingText(message) as T;
}

/**
 * Toasts are where a failed save shows its error. A gateway body must not
 * land in that sentence.
 */
function error(
  message: Parameters<typeof sonnerToast.error>[0],
  data?: ExternalToast,
) {
  const nextData =
    data && typeof data.description === 'string'
      ? { ...data, description: sanitizeUserFacingText(data.description) }
      : data;

  return sonnerToast.error(presentErrorText(message), nextData);
}

const toast = Object.assign(sonnerToast, { error });

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
